import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Calendar, Clock, User, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';

export const blogPosts = [
  {
    id: 'air-duct-cleaning-roseville-ca',
    title: 'Air Duct Cleaning in Roseville, CA — Is It Worth It? An Honest Answer from a Local HVAC Pro',
    excerpt: 'Before you spend money on air duct cleaning, read this honest guide. Learn when duct cleaning is necessary, when it\'s a waste, what it should cost in Roseville, and how to spot scams.',
    author: 'Juliano Romano',
    date: 'June 10, 2026',
    readTime: '10 min read',
    category: 'Air Duct Cleaning',
    keywords: 'air duct cleaning Roseville CA, duct cleaning Roseville, HVAC duct cleaning Roseville CA',
    image: 'https://images.pexels.com/photos/5463576/pexels-photo-5463576.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'hvac-maintenance-roseville-ca',
    title: 'HVAC Maintenance in Roseville, CA — The One Thing That Saves You Thousands (and Most People Skip It)',
    excerpt: 'The #1 reason HVAC systems die early is skipped maintenance. Here\'s the real cost comparison, what a tune-up includes, and why Roseville homeowners can\'t afford to skip it.',
    author: 'Juliano Romano',
    date: 'June 10, 2026',
    readTime: '9 min read',
    category: 'HVAC Maintenance',
    keywords: 'HVAC maintenance Roseville CA, AC tune-up Roseville, furnace maintenance Roseville CA',
    image: 'https://images.pexels.com/photos/5463571/pexels-photo-5463571.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'furnace-repair-roseville-ca',
    title: 'Furnace Repair in Roseville, CA — A No-Nonsense Guide from Your Local HVAC Guy',
    excerpt: 'Your furnace just quit and it\'s 38 degrees. Don\'t panic — here\'s everything you need to know about furnace repair in Roseville, from common problems and real costs to safety tips and DIY checks.',
    author: 'Juliano Romano',
    date: 'June 9, 2026',
    readTime: '10 min read',
    category: 'Furnace Repair',
    keywords: 'furnace repair Roseville CA, heater repair Roseville, furnace not working Roseville, heating repair Roseville CA',
    image: 'https://images.pexels.com/photos/7504837/pexels-photo-7504837.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'ac-repair-roseville-ca',
    title: 'AC Repair in Roseville, CA — When to Call, What to Expect, and How to Avoid Getting Overcharged',
    excerpt: 'Your AC just quit and it\'s 104 outside. Before you panic, read this honest guide from a Roseville HVAC owner with 20+ years of experience — common problems, real costs, and how to spot a bad contractor.',
    author: 'Juliano Romano',
    date: 'June 9, 2026',
    readTime: '12 min read',
    category: 'AC Repair',
    keywords: 'AC repair Roseville CA, air conditioner repair Roseville, HVAC repair Roseville CA, emergency AC repair Roseville',
    image: 'https://images.pexels.com/photos/5463587/pexels-photo-5463587.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'air-conditioner-service-roseville',
    title: 'Air Conditioner Service in Roseville, CA & Surrounding Cities — What Every Homeowner Should Know',
    excerpt: 'Honest advice from a local HVAC owner with 20+ years of experience. Learn when to service your AC, what to look for, and how to keep your home cool across Roseville, Rocklin, Folsom, and beyond.',
    author: 'Juliano Romano',
    date: 'June 2, 2026',
    readTime: '10 min read',
    category: 'Air Conditioner Service',
    keywords: 'air conditioner service Roseville CA, AC service Roseville, HVAC Roseville CA, air conditioner repair Rocklin Folsom',
    image: 'https://images.pexels.com/photos/5463571/pexels-photo-5463571.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    id: 'air-conditioning-service-roseville-ca',
    title: 'Air Conditioning Service Roseville CA: Complete Guide to Keeping Your Home Cool',
    excerpt: 'Everything Roseville homeowners need to know about professional air conditioning service — from seasonal maintenance and energy savings to emergency AC repair and choosing the right HVAC contractor.',
    author: 'Juliano Romano',
    date: 'June 2, 2026',
    readTime: '8 min read',
    category: 'Air Conditioning',
    keywords: 'air conditioning service Roseville CA, AC service Roseville, air conditioning repair Roseville CA, HVAC service Roseville CA',
    image: 'https://images.pexels.com/photos/5463576/pexels-photo-5463576.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
];

const BlogPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFCF7]" data-testid="blog-page">
      {/* Navigation Bar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            data-testid="back-to-home-btn"
            className="flex items-center gap-2 text-[#622B62] hover:text-[#DA9C3A] transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <a href={`tel:${companyInfo.phone}`}>
            <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold px-5 py-2 rounded-full">
              <Phone size={16} className="mr-2" />
              {companyInfo.phone}
            </Button>
          </a>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-[#622B62] text-white py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A3D7A] rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4A1F4A] rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h1 className="romano-heading text-4xl sm:text-5xl font-bold mb-4" data-testid="blog-heading">
            HVAC Tips & Insights
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Expert advice on air conditioning service, heating maintenance, and energy efficiency for Roseville CA homeowners.
          </p>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid gap-10">
          {blogPosts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.id}`}
              data-testid={`blog-card-${post.id}`}
              className="group bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <div className="grid md:grid-cols-5 gap-0">
                <div className="md:col-span-2 h-56 md:h-auto">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:col-span-3 p-8">
                  <span className="inline-block bg-[#622B62]/10 text-[#622B62] text-xs font-bold px-3 py-1 rounded-full mb-3">
                    {post.category}
                  </span>
                  <h2 className="romano-heading text-xl sm:text-2xl font-bold text-[#622B62] mb-3 group-hover:text-[#DA9C3A] transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 mb-4 leading-relaxed">{post.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><User size={14} /> {post.author}</span>
                    <span className="flex items-center gap-1"><Calendar size={14} /> {post.date}</span>
                    <span className="flex items-center gap-1"><Clock size={14} /> {post.readTime}</span>
                  </div>
                  <div className="mt-4 flex items-center gap-1 text-[#DA9C3A] font-semibold text-sm group-hover:gap-2 transition-all">
                    Read Full Article <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#622B62] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={companyInfo.logo} alt="Romano Heating & Cooling" className="h-24 w-24 object-contain rounded-lg" />
            <div className="text-left">
              <h3 className="font-bold romano-heading">Romano Heating & Cooling</h3>
              <p className="text-white/70 text-sm">1316 Church Street, Roseville, CA 95678</p>
            </div>
          </div>
          <p className="text-[#DA9C3A] font-semibold mb-2">(916) 274-9021</p>
          <p className="text-white/60 text-sm">&copy; {new Date().getFullYear()} Romano Heating and Cooling. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
